'use client';

import { forwardRef, useImperativeHandle, useState } from 'react';

import { PaymentMethodEnum } from '@/__generated_graphql__/types';
import { PaymentsModal } from '@/components/AdvertisePayments/components/Modal/Modal';
import { RendererModals } from '@/components/AdvertisePayments/components/Renderer/Renderer';
import { useContentTitle } from '@/components/AdvertisePayments/hooks/useContentTitle';
import { useModal } from '@/components/AdvertisePayments/hooks/useModal';
import { useModalController } from '@/components/AdvertisePayments/hooks/useModalController';
import { usePaymentHandler } from '@/components/AdvertisePayments/hooks/usePaymentHandler';
import { usePaymentModalsManager } from '@/components/AdvertisePayments/hooks/usePaymentModalsManager';
import { PaymentTypeEnum } from '@/constants/common';
import { CardAd } from '@/types';

import { ExtraContentModalProps } from './components/ExtraContent/ExtraContent.types';
import { useFetchPaidServices } from './hooks/useFetchPaidServices';
import { usePaymentSuccess } from './hooks/usePaymentSuccess';

export type AdvertisePaymentsProps = {
  mainModal: ReturnType<typeof useModal>;
  ad?: CardAd;
  type?: PaymentTypeEnum;
};

export type AdvertisePaymentsRef = {
  isSuccessTopUp: boolean;
  handleConfirmPayment: (
    method: PaymentMethodEnum,
    paidServiceOptionId: ExtraContentModalProps['activePaidServiceId']
  ) => void;
};

export const AdvertisePayments = forwardRef<
  AdvertisePaymentsRef,
  AdvertisePaymentsProps
>(({ mainModal, ad, type = PaymentTypeEnum.ADVERTISE }, ref) => {
  // @ts-expect-error ad
  const [transformedAd, setTransformedAd] = useState<CardAd>(ad);

  const modals = usePaymentModalsManager();
  const { PUBLICATION, ADVERTISE } = PaymentTypeEnum;

  const getContentTitle = useContentTitle();

  const modalController = useModalController({
    mainModal,
    uiModals: modals,
    type
  });

  const {
    uiModals,
    activeCard,
    reset,
    onAddBalance,
    isAddBalance,
    setIsAddBalance
  } = modalController;

  const fetchAndApplyPaidServices = useFetchPaidServices({
    updateCallback: paidServices =>
      setTransformedAd(prev => ({ ...prev, paidServices })),
    onLoading: modalController.setIsLoading
  });

  const { handleSuccess, isSuccessTopUp } = usePaymentSuccess({
    isAddBalance,
    transformedAd,
    type,
    mainModal,
    modals,
    successCallback: fetchAndApplyPaidServices
  });

  const paymentHandler = usePaymentHandler({
    type,
    adId: transformedAd?.id,
    ad: transformedAd,
    modals,
    mainModal,
    onSuccess: handleSuccess,
    paidServices: transformedAd?.paidServices
  });

  useImperativeHandle(ref, () => ({
    handleConfirmPayment: paymentHandler.handleConfirmPayment,
    isSuccessTopUp
  }));

  const contentTitle = getContentTitle(
    type === ADVERTISE ? activeCard : PUBLICATION,
    isAddBalance
  );

  const handleClose = () => {
    if (isAddBalance && type === PaymentTypeEnum.ADVERTISE)
      return setIsAddBalance(false);
    reset();
  };

  return (
    <>
      <RendererModals
        modals={uiModals}
        activeCard={activeCard}
        onRetry={paymentHandler.handleRetryPayment}
        onClose={handleClose}
        onAddBalance={onAddBalance}
      />

      <PaymentsModal
        ad={transformedAd}
        modalController={modalController}
        mainModal={mainModal}
        contentTitle={contentTitle}
        type={type}
        onConfirmPayment={paymentHandler.handleConfirmPayment}
      />
    </>
  );
});

AdvertisePayments.displayName = 'AdvertisePayments';
